import { PLAY_MODE, ATTR_INIT, FLIP_DEFAULT, STYLE_VALUE, ANIM_PROPERTY } from './constants';
import {
    PATH_ANIM_TYPES,
    animFillTypes, presetClassTypes,
} from '../../../slide/domain/ui-model/element-modeles/property/animation/constant';
import { getAnimTypeByPlayMode, getAnimClassByPlayMode, getTnRotate } from './utils';
import setFromStyle from './setFromStyle';
import setToStyle from './setToStyle';
import { setPathToStyle } from './setPathStyle';
import _has from 'lodash/has';
import { validTransforms } from '@src/js/common/anime.es';
import { getFinalToStyle } from './handleKeyframes';
import { AnimType } from '@tencent/slide-sdk-model';
import type { ComponentAnimWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-anim-wrapper';
import { IAnimModel, IAnimSimpleStyle } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { AnimCanvasProperty } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-constants';

enum ActionType {
    REPLACE = 'replace',
    PLUS = 'plus',
    MULTIPLY = 'multiply',
}

enum AnimDisplayType {
    IN_OPACITY = 'in_opacity',
    OUT_OPACITY = 'out_opacity',
    SCALE = 'scale',
    PATH = 'path',
    DEFAULT = 'default',
}

type StyleObject = {
    [key: string]: number | string;
};

// check透明度进入变更动画
function isInOpacityEffect(timeNode, playMode: PLAY_MODE = PLAY_MODE.play): boolean {
    const animType = getAnimTypeByPlayMode(timeNode, playMode);
    return animType === AnimType.FadeIn
           || animType === AnimType.Appear;
}

// check透明度退出变更动画
function isOutOpacityEffect(timeNode, playMode: PLAY_MODE = PLAY_MODE.play): boolean {
    const animType = getAnimTypeByPlayMode(timeNode, playMode);

    return animType === AnimType.FadeOut
           || animType === AnimType.Disappear;
}

const otherEffectKeyArr: AnimType[] = [
    AnimType.FlyOut,
    AnimType.FlyIn,
    AnimType.FloatUpIn,
    AnimType.FloatUpOut,
    AnimType.FloatDownIn,
    AnimType.FloatDownOut,
    AnimType.Plus,
    AnimType.Rotate,
    AnimType.Spread,
    AnimType.EnterRotate,
    AnimType.EnterScale,
    AnimType.ExitSpread,
    AnimType.ExitRotate,
    AnimType.ExitScale,
    AnimType.EntrCenterRevolve,
    AnimType.EntrGrowTurn,
    AnimType.EntrRiseUp,
    AnimType.EntrSpinner,
    AnimType.EntrBasicZoom,
    AnimType.EntrStretch,
    AnimType.ExitCenterRevolve,
    AnimType.ExitCollapse,
    AnimType.ExitShrinkTurn,
    AnimType.ExitSinkDown,
    AnimType.ExitSpinner,
    AnimType.ExitBasicZoom,
    AnimType.ExitStretch,
    AnimType.EntrBoomerang,
    AnimType.EntrPinwheel,
    AnimType.EntrBasicSwivel,
    AnimType.ExitBoomerang,
    AnimType.ExitPinwheel,
    AnimType.ExitBasicSwivel,
    AnimType.EmphTransparency,
    AnimType.EntrCredits,
    AnimType.ExitCredits,
    AnimType.EntrBlinds,
    AnimType.ExitBlinds,
    AnimType.FillColor,
    AnimType.FontColor,
    AnimType.LineColor,
    AnimType.BrushColor,
    AnimType.ComplementaryColor,
    AnimType.ComplementaryColor2,
    AnimType.ContrastingColor,
    AnimType.Darken,
    AnimType.Desaturate,
    AnimType.Lighten,
    AnimType.ObjectColor,
    AnimType.ColorPulse,
    AnimType.GrowWithColor,
    AnimType.EntrFloat,
    AnimType.ExitFloat,
    AnimType.CurveUp,
    AnimType.CurveDown,
    AnimType.CutIn,
    AnimType.CutOut,
    AnimType.EntrRandomBars,
    AnimType.ExitRandomBars,
    AnimType.DissolveIn,
    AnimType.DissolveOut,
    AnimType.EntrSplit,
    AnimType.ExitSplit,
    AnimType.EntrStrips,
    AnimType.ExitStrips,
    AnimType.EntrShapeCircle,
    AnimType.ExitShapeCircle,
    AnimType.EntrShapeBox,
    AnimType.ExitShapeBox,
    AnimType.EntrShapeDiamond,
    AnimType.ExitShapeDiamond,
    AnimType.EntrShapePlus,
    AnimType.ExitShapePlus,
    AnimType.EntrWedge,
    AnimType.ExitWedge,
    AnimType.EntrWheel,
    AnimType.ExitWheel,
    AnimType.SpiralIn,
    AnimType.SpiralOut,
    AnimType.EntrBounce,
    AnimType.ExitBounce,
];

function isOtherEffect(timeNode, playMode: PLAY_MODE = PLAY_MODE.play): boolean {
    const animType = getAnimTypeByPlayMode(timeNode, playMode);
    return otherEffectKeyArr.includes(animType);
}

function isPathEffect(timeNode, playMode: PLAY_MODE = PLAY_MODE.play): boolean {
    const animType = getAnimTypeByPlayMode(timeNode, playMode);
    return Object.values(PATH_ANIM_TYPES).includes(animType);
}

export function defaultStyle() {
    const style = { opacity: 1 };
    validTransforms.forEach((k) => {
        if (k === 'perspective' || k.indexOf('Z') > -1) return;
        if (k.indexOf('scale') > -1) style[k] = 1;
        else style[k] = 0;
    });
    return style;
}

const getScaleFromStyle = (timeNode, playMode: PLAY_MODE) => {
    if (playMode !== PLAY_MODE.edit) return ATTR_INIT.scaleInit;
    return {
        scaleX: timeNode.flipH === FLIP_DEFAULT ? STYLE_VALUE.negativeNum : STYLE_VALUE.positiveNum,
        scaleY: timeNode.flipV === FLIP_DEFAULT ? STYLE_VALUE.negativeNum : STYLE_VALUE.positiveNum,
    };
};

// 获取单个动画的fromStyle
export function initFromStyle(timeNode, playMode:  PLAY_MODE = PLAY_MODE.play, animTarget: ComponentAnimWrapper) {
    if (!timeNode) return {};
    const tnRotate = getTnRotate(timeNode);
    const defaultFromStyle = { ...defaultStyle(), rotate: tnRotate };
    const presetClass = getAnimClassByPlayMode(timeNode, playMode);
    if (presetClass === presetClassTypes.PATH) return defaultFromStyle;

    const fromStyleMap: { [key in AnimType]? } = {
        [AnimType.FadeIn]: ATTR_INIT.opacityHide,
        [AnimType.Appear]: ATTR_INIT.opacityHide,
        [AnimType.FadeOut]: ATTR_INIT.opacityShow,
        [AnimType.Disappear]: ATTR_INIT.opacityShow,
    };
    const animType = getAnimTypeByPlayMode(timeNode, playMode);
    const tempFromStyle = fromStyleMap[animType];
    if (tempFromStyle) return { ...defaultFromStyle, ...tempFromStyle };

    if (animType === AnimType.Scale) return { ...defaultFromStyle, ...getScaleFromStyle(timeNode, playMode) };

    if (otherEffectKeyArr.includes(animType)) return {
        ...defaultFromStyle,
        ...setFromStyle(timeNode, playMode, animTarget),
    };

    return {};
}

const getScaleToStyle = (timeNode, playMode: PLAY_MODE) => {
    if (playMode !== PLAY_MODE.edit) return {
        scaleX: timeNode.scaleX,
        scaleY: timeNode.scaleY,
    };
    const byX = timeNode.props.by.x;
    const byY = timeNode.props.by.y;
    return {
        scaleX: timeNode.flipH === FLIP_DEFAULT ? -byX : byX,
        scaleY: timeNode.flipV === FLIP_DEFAULT ? -byY : byY,
    };
};

// 获取单个动画的toStyle
export function initToStyle(timeNode, playMode: PLAY_MODE = PLAY_MODE.play) {
    if (!timeNode) return {};
    const toStyleMap: { [key in AnimType]? } = {
        [AnimType.FadeIn]: ATTR_INIT.opacityShow,
        [AnimType.Appear]: ATTR_INIT.opacityShow,
        [AnimType.FadeOut]: ATTR_INIT.opacityHide,
        [AnimType.Disappear]: ATTR_INIT.opacityHide,
    };

    const animType = getAnimTypeByPlayMode(timeNode, playMode);
    const tempToStyle = toStyleMap[animType];
    if (tempToStyle) return tempToStyle;

    if (animType === AnimType.Scale) return getScaleToStyle(timeNode, playMode);
    if (otherEffectKeyArr.includes(animType)) {
        return setToStyle(timeNode, playMode);    // 此方法不是timeNode的属性累加，所以直接替换也正确
    }
    return {};
}

// 合并tempStyles的属性到sytles中
function assignObjFormTempObj(sytles, tempStyles) {
    const returnSytles = sytles;
    Object.keys(tempStyles).forEach((key) => {
        if ({}.hasOwnProperty.call(tempStyles, key)) {
            returnSytles[key] = tempStyles[key];
        }
    });
    return returnSytles;
}

// 合并tempStyles的属性到sytles中
function mergeObjFormTempObj(sytles, tempStyles) {
    const returnSytles = sytles;
    Object.keys(tempStyles).forEach((key) => {
        if ({}.hasOwnProperty.call(tempStyles, key)) {
            if (!returnSytles[key]) {
                returnSytles[key] = tempStyles[key];
            } else {
                const { ROTATE, TRANSLATE_X, TRANSLATE_Y, OPACITY, SCALE_X, SCALE_Y } = ANIM_PROPERTY;
                if (key === ROTATE || key === TRANSLATE_X || key === TRANSLATE_Y) {
                    returnSytles[key] += tempStyles[key];
                } else if (key === OPACITY || key === SCALE_X || key === SCALE_Y) {
                    returnSytles[key] *= tempStyles[key];
                }
            }
        }
    });
    return returnSytles;
}

// 获取一组动画到每index结点的toStyles
export function initToStyles(timeNodes, index: number, playMode: PLAY_MODE = PLAY_MODE.play) {
    let toSytles: StyleObject = {};
    if (!timeNodes || timeNodes.length === 0) {
        return toSytles;
    }

    for (let i = 0; i <= index; i++) {
        const timeNode = timeNodes[i];
        const objRotate = getTnRotate(timeNode);
        const animType = getAnimTypeByPlayMode(timeNode, playMode);
        if (isInOpacityEffect(timeNode, playMode)) {
            toSytles.opacity = 1;
        } else if (isOutOpacityEffect(timeNode, playMode)) {
            toSytles.opacity = 0;
        } else if (animType === AnimType.Scale) {
            toSytles = {
                ...toStylesOfScale(i, index, toSytles, timeNode),
                rotate: objRotate,
            };
        } else if (isPathEffect(timeNode, playMode)) {
            const tempStyles = setPathToStyle(timeNode, playMode);
            toSytles = assignObjFormTempObj(toSytles, tempStyles);
        } else if (isOtherEffect(timeNode, playMode)) {
            const tempStyles = setToStyle(timeNode, playMode);
            toSytles = mergeObjFormTempObj(toSytles, getFinalToStyle(tempStyles));
        }
    }
    return toSytles;
}

/**
 * 合并样式对象
 * @param targetStyle 目标对象
 * @param sourceStyle 源对象
 * @returns 合并后的样式对象 IAnimSimpleStyle
 */
function mergeStyle(targetStyle: IAnimSimpleStyle, sourceStyle: IAnimSimpleStyle): IAnimSimpleStyle {
    const finalStyle: IAnimSimpleStyle = targetStyle;
    const propertyActionMap = {
        ROTATE: ActionType.PLUS,
        TRANSLATE_X: ActionType.PLUS,
        TRANSLATE_Y: ActionType.PLUS,
        OPACITY: ActionType.MULTIPLY,
        SCALE_X: ActionType.MULTIPLY,
        SCALE_Y: ActionType.MULTIPLY,
        [AnimCanvasProperty.FILL_COLOR]: ActionType.REPLACE,
        [AnimCanvasProperty.FONT_COLOR]: ActionType.REPLACE,
        [AnimCanvasProperty.LINE_COLOR]: ActionType.REPLACE,
    };

    const handlerMap = {
        [ActionType.REPLACE]: (key, value) => {
            finalStyle[key] = value;
        },
        [ActionType.PLUS]: (key, value) => {
            finalStyle[key] += value;
        },
        [ActionType.MULTIPLY]: (key, value) => {
            finalStyle[key] *= value;
        },
    };

    Object.entries(sourceStyle).forEach(([key, value]) => {
        if (!finalStyle[key]) {
            finalStyle[key] = value;
            return;
        }
        const handler = handlerMap[propertyActionMap[key]];
        handler?.(key, value);
    });

    return finalStyle;
}

/**
 * 获取播放完第order组动画后元素的最终样式
 * @param animModelList IAnimModel[]
 * @param order 动画播放顺序
 * @returns IAnimSimpleStyle
 */
export function getToStylesOfOrder(animModelList: IAnimModel[], order: number): IAnimSimpleStyle {
    if (!animModelList?.length) return {};
    let toStyles: IAnimSimpleStyle = {};

    const animTypeMap = {
        [AnimType.FadeIn]: AnimDisplayType.IN_OPACITY,
        [AnimType.Appear]: AnimDisplayType.IN_OPACITY,
        [AnimType.FadeOut]: AnimDisplayType.OUT_OPACITY,
        [AnimType.Disappear]: AnimDisplayType.OUT_OPACITY,
        [AnimType.Scale]: AnimDisplayType.SCALE,
    };

    const handlerMap = {
        [AnimDisplayType.IN_OPACITY]: () => {
            toStyles.opacity = 1;
        },
        [AnimDisplayType.OUT_OPACITY]: () => {
            toStyles.opacity = 0;
        },
        [AnimDisplayType.SCALE]: (index: number) => {
            const animModel = animModelList[index];
            toStyles = {
                ...toStylesOfScale(index, order, toStyles, animModel),
                rotate: animModel?.elementModel?.props?.style?.rotate || 0,
            };
        },
        [AnimDisplayType.PATH]: (index: number) => {
            const animModel = animModelList[index];
            const tempStyles = setPathToStyle(animModel, PLAY_MODE.edit);
            toStyles = assignObjFormTempObj(toStyles, tempStyles);
        },
        [AnimDisplayType.DEFAULT]: (index) => {
            const animModel = animModelList[index];
            const tempStyles = setToStyle(animModel, PLAY_MODE.edit);
            toStyles = mergeStyle(toStyles, getFinalToStyle(tempStyles));
        },
    };

    for (let i = 0; i <= order; i++) {
        const animModel = animModelList[i];
        const { animType } =  animModel.props;
        const handler = handlerMap[animTypeMap[animType]] || handlerMap[AnimDisplayType.DEFAULT];
        handler?.(i);
    }

    return toStyles;
}

// 矫正下标为index的缩放类属性
function toStylesOfScale(idx: number, index: number, toSytles, timeNode) {
    let tempToSytles = toSytles;
    if (idx === index) {
        const nodeStyle = initToStyle(timeNode, PLAY_MODE.edit);
        if (!_has(tempToSytles, 'scaleX')) {  // 没有缩放属性，则默认1, 否则保持原值
            tempToSytles.scaleX = nodeStyle.scaleX;
        } else {
            tempToSytles.scaleX = nodeStyle.scaleX * tempToSytles.scaleX;
        }
        if (!_has(tempToSytles, 'scaleY')) {
            tempToSytles.scaleY = nodeStyle.scaleY;
        } else {
            tempToSytles.scaleY = nodeStyle.scaleY * tempToSytles.scaleY;
        }
    } else {
        tempToSytles = toStylesScaleWithRemove(tempToSytles, timeNode);
    }
    return tempToSytles;
}

// fillType为remove的缩放类动画的属性回溯
function toStylesScaleWithRemove(toSytles, timeNode) {
    const tempToSytles = toSytles;
    if (timeNode.fill === animFillTypes.REMOVE) {
        if (!_has(tempToSytles, 'scaleX')) {  // 没有缩放属性，则默认1, 否则保持原值
            tempToSytles.scaleX = 1;
        }
        if (!_has(tempToSytles, 'scaleY')) {
            tempToSytles.scaleY = 1;
        }
    } else {
        if (!_has(tempToSytles, 'scaleX')) {  // 没有缩放属性，则默认1, 否则保持原值
            tempToSytles.scaleX = timeNode.scaleX;
        } else {
            tempToSytles.scaleX = timeNode.scaleX * tempToSytles.scaleX;
        }
        if (!_has(tempToSytles, 'scaleY')) {
            tempToSytles.scaleY = timeNode.scaleY;
        } else {
            tempToSytles.scaleY = timeNode.scaleY * tempToSytles.scaleY;
        }
    }
    return tempToSytles;
}
